import { faTrashAlt, faUpload } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MenuItem, Switch, Typography } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import usePresentations from '../../../../../../../hooks/usePresentations';
import { Meetingroom } from '../../../../../../../interfaces/interfaces';
import { fetchAndSetSlots } from '../../../../../../../reduxToolkit/slices/commonSlice';
import { useSelector, useDispatch } from '../../../../../../../reduxToolkit/typedRedux';
import { createDisplayLink } from '../../../../../../../services/helper';
import { fileUpload, updateMeeting } from '../../../../../../../services/services';
import VyButton from '../../../../../../common/VyButton';
import VySelect from '../../../../../../common/VySelect';
import VyTextField from '../../../../../../common/VyTextField';
import VyTooltip from '../../../../../../common/VyTooltip';
import { VyUploadCrop } from '../../../../../../common/VyUploadCrop';

const PresentationEditComponent = (props: { meetingroom: Meetingroom; useList?: boolean; useBooth?: boolean }) => {
  const { meetingroom, useList = false, useBooth = false } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { register, handleSubmit, control, formState, reset } = useForm();
  const { getPlaceholderBackgroundImage } = usePresentations();

  const event = useSelector(({ common }) => common.event);

  const [currentMeeting, setCurrentMeeting] = useState(meetingroom);
  const [requiredSignup, setRequiredSignup] = useState(currentMeeting!.presentationRequireSignup ?? meetingroom.presentationRequireSignup);

  async function handleUpdate(body: any) {
    if (!currentMeeting) return;

    try {
      currentMeeting;
      const { data: data } = await updateMeeting({
        meetingroomid: currentMeeting._id,
        ...currentMeeting,
        ...body,
        presentationMaxSeats: body.presentationMaxSeats ? parseInt(body.presentationMaxSeats) : currentMeeting.presentationMaxSeats,
      });
      setCurrentMeeting(data);
      dispatch(fetchAndSetSlots(event?._id));
      toast.success(t('common:success'));
      if (formState.isDirty) reset(body);
    } catch (err) {
      toast.error(t('common:error-without-status'));
    }
  }

  async function uploadCroppedImage(blob: Blob, name: string) {
    if (!currentMeeting) return;
    const data = await fileUpload(blob);
    await handleUpdate({ [name]: data.data.fileId });
  }

  async function deletePresentationBackground() {
    if (!currentMeeting) return;
    await handleUpdate({ presentationBackground: null });
  }

  async function deleteSpeakerImage() {
    if (!currentMeeting) return;
    await handleUpdate({ image: null });
  }

  async function submit(props: any) {
    await handleUpdate(props);
  }

  if (useList) {
    return (
      <form onSubmit={handleSubmit(submit)} className="flex overflow-y-scroll m-5 mt-8 w-full">
        {/* <Controller
          control={control}
          name={`contactperson`}
          defaultValue={currentMeeting.contactperson}
          render={({ field }) => <VyTextField {...field} className="m-3 w-5/6" label={t('account:presentation.contactperson')} />}
        />
        <Controller
          control={control}
          name={`name`}
          defaultValue={currentMeeting.name}
          render={({ field }) => <VyTextField {...field} className="m-3 w-5/6" label={t('account:presentation.title')} />}
        />
        <VyTextField className="m-3 w-5/6" defaultValue={currentMeeting.name} label={t('account:presentation.title')} register={register('name')} /> */}
        <VyButton className="m-3" disabled={!formState.isDirty} type="submit" isSaveButton>
          {t('common:save')}
        </VyButton>
      </form>
    );
  }

  return (
    <form onSubmit={handleSubmit(submit)}>
      <div
        style={{
          backgroundImage: `linear-gradient(20deg, rgba(0, 0, 0, 0.4) 0%, rgba(150, 150, 150, 0.3) 60%, rgba(238, 238, 238, 0.2) 100%), url(${
            currentMeeting.presentationBackground
              ? createDisplayLink(currentMeeting.presentationBackground, false)
              : getPlaceholderBackgroundImage(currentMeeting)
          })`,
        }}
        className="absolute top-0 left-0 w-full h-full bg-transparent bg-center bg-no-repeat bg-cover pointer-events-none"
      />
      <div className={`relative z-20 max-w-lg overflow-auto cursor-default ${!useBooth ? 'm-5 mt-8' : 'mb-5'} space-y-4`}>
        <div className="flex items-center">
          <VyUploadCrop onImage={(blob) => uploadCroppedImage(blob, 'image')}>
            <div className="flex items-center">
              <div
                className="border border-white cursor-pointer logo-wrapper"
                style={{ backgroundImage: `url(${createDisplayLink(currentMeeting.image, false)})` }}></div>
              <div className="flex items-center cursor-pointer">
                <p style={{ color: 'white', marginLeft: '1vh' }}>{t('account:image-speaker')}</p>
                <FontAwesomeIcon style={{ cursor: 'pointer', height: '14px', width: '14px', color: 'white', marginLeft: '0.5rem' }} icon={faUpload} />
              </div>
            </div>
          </VyUploadCrop>
          {currentMeeting.image && (
            <label className="ml-1 cursor-pointer gray-bg" onClick={deleteSpeakerImage}>
              <FontAwesomeIcon style={{ height: '14px', width: '14px', color: 'white' }} icon={faTrashAlt} />
            </label>
          )}
        </div>
        <div className="flex flex-row my-2 cursor-pointer w-fit">
          <VyUploadCrop onImage={(blob) => uploadCroppedImage(blob, 'presentationBackground')}>
            <div>
              <span style={{ color: 'white' }}>{t('account:upload-background-image')}</span>
              <FontAwesomeIcon style={{ height: '14px', width: '14px', color: 'white', marginLeft: '0.5rem' }} icon={faUpload} />
            </div>
          </VyUploadCrop>
          {currentMeeting?.presentationBackground && (
            <label className="ml-1 cursor-pointer gray-bg" onClick={() => deletePresentationBackground()}>
              <FontAwesomeIcon style={{ height: '14px', width: '14px', color: 'white' }} icon={faTrashAlt} />
            </label>
          )}
        </div>
        {!useBooth && (
          <VySelect
            value={currentMeeting.presentationSlotType}
            sx={{
              color: 'white',
              '.MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(228, 219, 233, 0.25)',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(228, 219, 233, 0.25)',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(228, 219, 233, 0.25)',
              },
              '.MuiSvgIcon-root ': {
                fill: 'white !important',
              },
            }}
            onChange={(e: any) => handleUpdate({ presentationSlotType: e.target.value })}>
            {[
              {
                value: 'online',
                title: <VyTooltip title={t('account:presentation-types.online-tooltip')}>{t('account:presentation-types.online')}</VyTooltip>,
              },
              {
                value: 'on-site',
                title: <VyTooltip title={t('account:presentation-types.on-site-tooltip')}>{t('account:presentation-types.on-site')}</VyTooltip>,
              },
              {
                value: 'hybrid',
                title: <VyTooltip title={t('account:presentation-types.hybrid-tooltip')}>{t('account:presentation-types.hybrid')}</VyTooltip>,
              },
            ].map((_) => (
              <MenuItem key={_.value} value={_.value}>
                <p>{!_.value ? <span className="italic text-slate-400">{_.title || _.value}</span> : _.title || _.value}</p>
              </MenuItem>
            ))}
          </VySelect>
        )}
        <Controller
          control={control}
          name={`contactperson`}
          defaultValue={currentMeeting.contactperson}
          render={({ field }) => <VyTextField {...field} borderColor="white" labelColor="white" label={t('account:presentation.contactperson')} />}
        />
        <Controller
          control={control}
          name={`name`}
          defaultValue={currentMeeting.name}
          rules={{ required: t('form:fieldrequired') }}
          render={({ field, fieldState: { error } }) => (
            <VyTextField {...field} borderColor="white" labelColor="white" label={t('account:presentation.title')} error={!!error} errorText={error?.message} />
          )}
        />
        {currentMeeting?.presentationSlotType !== 'online' && !useBooth && (
          <Controller
            control={control}
            name={`presentationMaxSeats`}
            defaultValue={currentMeeting.presentationMaxSeats}
            render={({ field }) => (
              <VyTextField {...field} borderColor="white" labelColor="white" label={t('account:presentation.seats-number')} type="number" />
            )}
          />
        )}
        {event?.settings.enableOnlinePresentationBooking && currentMeeting?.meetingtype === 'presentation' && (
          <div className="flex justify-between items-center w-full text-white">
            <Typography>{t('config:mustRegister-title')}</Typography>
            <Switch checked={requiredSignup} {...register('presentationRequireSignup')} onChange={(e) => setRequiredSignup(e.target.checked)} />
          </div>
        )}
        <VyButton className="mt-2 w-full" type="submit" isSaveButton>
          {t('common:save')}
        </VyButton>
      </div>

      <style>{`
    .logo-wrapper {
      width: 80px;
      height: 80px;
      background-size: cover;
      background-position: center;
      background-color: transparent;
      border-radius: 50%;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
    }
    .logo-upload {
      display: flex;
      flex-direction: column;
      background-color: rgba(57, 57, 57, 0.4);
      justify-content: center;
      align-items: center;
      font-size: 10px;
      width: 50px;
    }
    .logo-upload p {
      text-align: center;
      word-wrap: wrap;
      font-size: 8px;
      color: white;
    }
    `}</style>
    </form>
  );
};
export default PresentationEditComponent;
